/* eslint-disable no-console */
import {BaseError} from "@/components/Errors.tsx";
import {
    IconAlertTriangleOff,
    IconCheck,
    IconCopy,
    IconFileSad,
} from "@tabler/icons-react";
import React, {ReactNode} from "react";
import {ActionIcon, Button, CopyButton, Group, Stack, Text, Tooltip} from "@mantine/core"
import {Location} from "react-router-dom";

interface ErrorBoundaryProps {
    children: ReactNode
    location: Location
}

interface ErrorBoundaryCleanState {hasError: false, error: null}
interface ErrorBoundaryErrorState {hasError: true, error: Error}
type ErrorBoundaryState = ErrorBoundaryCleanState | ErrorBoundaryErrorState

export default class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props)
        this.state = {hasError: false, error: null}
    }

    static getDerivedStateFromError(error: Error) {
        return {hasError: true, error: error}
    }

    componentDidCatch(error: Error) {
        console.error(error)
    }

    clearError() {
        this.setState({hasError: false})
    }

    componentDidUpdate(prevProps: ErrorBoundaryProps) {
        if (prevProps.location != this.props.location) {
            this.clearError()
        }
    }

    render() {
        if (this.state.hasError) {
            return <Stack mt={"xl"}>
                <BaseError icon={<IconFileSad size={"4em"}/>} code={""} name={"Unknown Error"} message={<Text>An unexpected error has occured in the page you were viewing. Please use the Copy Error button below to report this issue to the Omni developers.</Text>} otherActions={<Group>
                    <CopyButton value={this.state.error?.stack ?? ""}>
                        {({copied, copy}) => (
                            <Button color={copied ? "green" : "blue"} variant={"subtle"} size={"md"} onClick={copy} leftSection={copied ? <IconCheck/> : <IconCopy/>}>
                                {copied ? "Copied!" : "Copy Error"}
                            </Button>
                        )}
                    </CopyButton>
                    <Tooltip label={"Retry/Clear Error"}>
                        <ActionIcon variant={"subtle"} size={"xl"} color={"orange"} onClick={this.clearError.bind(this)}><IconAlertTriangleOff/></ActionIcon>
                    </Tooltip>
                </Group>}/>
            </Stack>
        }
        return this.props.children
    }
}